import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Router, Switch, Route, NavLink, useRouteMatch, Link, Redirect } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import StatefulNavLink from 'components/StatefulNavLink'
import Basics from './components/Basics'
import Nda from './components/Nda'
import YourVoice from './components/YourVoice'
import Skills from './components/Skills'
import SettingTheScene from './components/SettingTheScene'
import Demos from './components/Demos'
import Review from './components/Review'
import Rates from './components/Rates'
import PaymentBanking from './components/PaymentBanking'
import Availability from './components/Availability'
import Help from './components/Help'
import messages from './messages'
import Icon from 'components/Icon'
import { Button } from '@arnsbogroup/react-voicearchive'
import LocaleSelect from 'components/LocaleSelect'
import useRoutes from 'hooks/useRoutes'

const SupplierProfile = ({ history, match, location }) => {
  const parentRef = useRef()
  const { formatMessage } = useIntl()
  const [initialState, setInitialState] = useState({})
  const [loading, setLoading] = useState(false)
  const { setRoutes, routes } = useRoutes()


  useEffect(() => {
    if (document.querySelector('.b24-widget-button-wrapper')) {
      document.querySelector('.b24-widget-button-wrapper').style.bottom = '100px'
    }

    return () => {
      if (document.querySelector('.b24-widget-button-wrapper')) {
        document.querySelector('.b24-widget-button-wrapper').style.bottom = '50px'
      }
    }
  }, [])


  const [supplier, setSupplier] = useState({
    stage: null,
    status: null,
  })

  const [steps, setSteps] = useState({
    'basics': {
      name: formatMessage(messages.basics),
      title: formatMessage(messages.basicsHint),
      icon: 'user-edit',
      path: 'basics',
      component: Basics,
      unlocked: true,
      complete: false,
    },
    'nda': {
      name: formatMessage(messages.collaborationAndNda),
      title: formatMessage(messages.collaborationAndNdaHint),
      icon: 'file-signature',
      path: 'nda',
      component: Nda,
      unlocked: false,
      complete: false,
      full: true,
    },
    'your-voice': {
      name: formatMessage(messages.yourVoice),
      title: formatMessage(messages.yourVoiceHint),
      icon: 'waveform',
      path: 'your-voice',
      component: YourVoice,
      unlocked: false,
      complete: false,
    },
    'skills-and-setup': {
      name: formatMessage(messages.skillsAndSetup),
      title: formatMessage(messages.skillsAndSetupHint),
      icon: 'computer-speaker',
      path: 'skills-and-setup',
      component: Skills,
      unlocked: false,
      complete: false,
    },
    'demos': {
      name: formatMessage(messages.demos),
      title: formatMessage(messages.demosHint),
      icon: 'microphone-alt',
      path: 'demos',
      component: Demos,
      unlocked: false,
      complete: false,
    },
    'review': {
      name: formatMessage(messages.review),
      title: formatMessage(messages.reviewHint),
      icon: 'tasks',
      path: 'review',
      component: Review,
      unlocked: false,
      complete: false,
    },
    'rates': {
      name: formatMessage(messages.rates),
      title: formatMessage(messages.ratesHint),
      icon: 'envelope-open-dollar',
      path: 'rates',
      component: Rates,
      unlocked: false,
      complete: false,
    },
    'payment-and-banking-details': {
      name: formatMessage(messages.paymentBanking),
      title: formatMessage(messages.paymentBankingHint),
      icon: 'funnel-dollar',
      path: 'payment-and-banking-details',
      component: PaymentBanking,
      unlocked: false,
      complete: false,
    },
    'availability': {
      name: formatMessage(messages.availability),
      title: formatMessage(messages.availabilityHint),
      icon: 'calendar-alt',
      path: 'availability',
      component: Availability,
      unlocked: false,
      complete: false,
      optional: false,
    },
  })

  const callbackRef = useRef(() => Promise.resolve())

  useEffect(() => {
    checkState()
  }, [])

  const { path, url } = useRouteMatch()

  const currentRouteIndex = Object.values(routes)
    .findIndex(({ path }) => useRouteMatch(`${url}/${path}`))

  const currentRoute = Object.values(routes)[currentRouteIndex]

  const setCallback = (callback) => {
    callbackRef.current = (event) => {
      setLoading(true)
      return new Promise((resolve, reject) => (
        Promise.resolve(callback(event))
        .then(checkState)
        .then(resolve)
        .catch(reject)
        .finally(() => setLoading(false))
      ))
    }
  }

  const checkState = () => {
    return fetch(`/rest/v2/supplier/${match.params.supplierId}/state`, { credentials: 'same-origin' }) 
      .then((response) => response.json())
      .then(({ steps, ...supplier }) => {
        setSupplier(supplier)
        setSteps((prevSteps) =>  Object.entries(steps)
          .map(([key, step]) => ({ ...prevSteps[key], ...step, }))
          .reduce((memo, step) => {
            memo[step.path] = step
            return memo
          }, {}))
      })
  }

  const onSave = (event) => {
    const complete = supplier.stage === 'NEW' &&
      supplier.status === 'ACCEPTED' &&
      currentRoute.path =='payment-and-banking-details';

    if (!callbackRef || typeof callbackRef.current !== 'function') {
      event.preventDefault()
      event.stopPropagation()
      window.alert('Unable to save. Please try again.')
      return;
    }

    return callbackRef.current(event).then(() => {
      if (supplier.stage === 'ONBOARDED') {
        // No op
      } else if (complete) {
        fetch(`/rest/v2/supplier/${match.params.supplierId}/complete`, {
          method: 'PUT',
          credentials: 'same-origin',
        })
          .then((response) => response.json())
          .then(({ success, errors }) => {
            if (!success) {
              return Promise.reject(Object.values(errors).join(', '))
            }
          })
      }
    }).catch((err) => {
      event.preventDefault()
      event.stopPropagation()
      window.alert(err)
    })
  }

  useEffect(() => {
    let nextSteps = [...Object.values(steps), {
      name: formatMessage(messages.help),
      href: `mailto:${formatMessage(messages.helpEmail)}`,
      icon: 'life-ring',
      path: 'help',
      component: Help,
      unlocked: true,
      complete: true,
    }]

    if (supplier.stage === 'ONBOARDED' || supplier.stage !== 'NEW') {
      nextSteps.push({
        path: '/dashboard',
        unlocked: true,
        name: formatMessage(messages[loading ? 'loading' : supplier.stage !== 'NEW' ? 'save' : 'saveAndReturn']),
        icon: loading ? 'spinner-third' : 'save',
        cta: true,
      })
    }

    nextSteps = nextSteps.map((route) => ({
      ...route,
      name: 'complete' in route ?
        <span>{route.name} <Icon className="ml-2" icon={route.complete ? 'check' : route.unlocked ? 'hourglass' : 'lock'} /></span> :
        <span>{route.name}</span>,
      disabled: 'unlocked' in route ? !route.unlocked : false,
      onClick: (event) => {
        if ('unlocked' in route && !route.unlocked) {
          event.preventDefault()
          event.stopPropagation()
        } else if (route.unlocked) {
          onSave(event)
        }
      }
    }))

    setRoutes(nextSteps)

    return () => {
      setRoutes([])
    }
  }, [steps, supplier, loading])

  return (
    <div className="h-full w-full flex flex-col">
      <div className="grow flex justify-between overflow-scroll" ref={parentRef}>
        <Switch>
          <Redirect exact from={url} to={`${url}/basics`} />
          { Object.values(routes)
              .filter(({ component }) => component)
              .map(({ unlocked, path, component: Component, full = false }) => (
            <Route key={`${path}-component`} path={`${url}/${path}`}>
              { unlocked ? (
                <div className={full ? 'w-full h-full' : 'w-11/12 md:w-9/12 lg:2/3 xl:w-1/2 m-auto py-20'}>
                  <Component
                    match={match}
                    location={location}
                    setCallback={setCallback}
                    supplier={supplier}
                    parentRef={parentRef}
                  />
                </div>
              ) : (
                <div className="w-full h-full justify-center items-center">
                  <p><FormattedMessage {...messages.inaccessible} /></p>
                </div>
              ) }
            </Route>
          )) }
        </Switch>
      </div>
      <div className="z-50 bg-white">
        <div className="border-t border-gray-400 w-full">
          <div className={`border-t-2 border-primary w-${ currentRouteIndex + 3 }/12`} />
        </div>

        <div className="flex justify-between items-center py-4 px-10">
          { currentRouteIndex > 0 &&
            <Link to={`${url}/${Object.values(routes)[currentRouteIndex - 1].path}`} onClick={onSave}>
              <Button variant="outline">
                <FormattedMessage {...messages.previous} />
              </Button>
            </Link>
          }

          <div className="text-gray-800">
            <FormattedMessage {...messages.progress} values={{
              x: currentRouteIndex + 1,
              y: Object.keys(routes).length,
            }} />
          </div>

          { currentRouteIndex < Object.keys(routes).length - 1 && currentRouteIndex + 2 < Object.keys(routes).length ? (
            <Link
              to={`${url}/${Object.values(routes)[currentRouteIndex + 1].path}`}
              onClick={onSave}
            >
              <Button>
                <FormattedMessage {...messages.next } />
              </Button>
            </Link>
          ) : (
            <a href="/dashboard">
              <Button>
                <FormattedMessage {...messages[currentRouteIndex + 2 >= Object.keys(routes).length ? 'complete' : 'saveAndReturn']} />
              </Button>
            </a>
          ) }
        </div>
      </div>
    </div>
  )
}

export default SupplierProfile
